import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Grid,
  Column,
  FormGroup,
  Tile,
  Stack,
  ToastNotification,
  Button,
  FileUploader,
  Loading,
  Tag,
} from "@carbon/react";
import {
  Document,
  TrashCan,
  View,
} from "@carbon/icons-react";
import CommonFields from "../../common/CommonField";
import { callApi } from "../../../../services/apiService";

const AdditionalInfo = ({
  propertyUuid = "",
  data = {},
  attributeValues = [],
  createProperty,
  saveSectionAttributes,
  onSaveSuccess,
  onComplete,
  onSaved,
  CommonButtons,
  onNavigateNext,
}) => {

  // DEBUG: Log all props on component mount and updates
  console.log('🔍 AdditionalInfo Props:', {
    propertyUuid,
    hasData: !!data && Object.keys(data).length > 0,
    attributeValuesCount: attributeValues.length,
    dataKeys: data ? Object.keys(data) : [],
    attributes: data?.attributes,
    additionalInfo: data?.additional_information
  });

  // ------------------------------ STATE
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Existing documents state
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [deletingDocId, setDeletingDocId] = useState(null);

  // Track which fields have been uploaded already
  const [uploadedFields, setUploadedFields] = useState({});
  // const S3_BASE_URL = "https://vaasalbucket.s3.ap-south-1.amazonaws.com/";

  // const getFullDocumentUrl = (url = "") => {
  //   if (!url) return "";
  //   if (url.startsWith("http")) return url;
  //   const normalized = url.replace(/^property\/documents\//, "documents/property/");
  //   return `${S3_BASE_URL}${normalized}`;
  // };

  // Form values
  const [form, setForm] = useState({
    saledeedcertificate: "",
    saleagreement: "",
    propertytaxpaid: "",
    occupancycertificate: "",
    otherdocuments: "",
    allotmentLetter: "", 
  });

  // Document upload state for each field
  const [documentUploads, setDocumentUploads] = useState({
    saledeedcertificate: { files: [] },
    saleagreement: { files: [] },
    propertytaxpaid: { files: [] },
    occupancycertificate: { files: [] },
    allotmentLetter: { files: [] }, 
    otherdocuments: { files: [] },
  });

  // ------------------------------ MAPPING
  const FIELD_TO_ATTRIBUTE_MAP = useMemo(
    () => ({
      saledeedcertificate: "resale_sale_deed_certificate", 
      saleagreement: "resale_sale_agreement",
      propertytaxpaid: "resale_property_tax_paid",
      occupancycertificate: "resale_occupancy_certificate",
      otherdocuments: "resale_other_documents",
      allotmentLetter: "resale_allotment_letter",
  
    }),
    []
  );

  const FIELD_TO_DOCUMENT_TYPE = useMemo(
    () => ({
      saledeedcertificate: "Sale Deed Certificate",
      saleagreement: "Sale Agreement",
      propertytaxpaid: "Property Tax Receipt",
      occupancycertificate: "Occupancy Certificate",
      otherdocuments: "Other Documents",
      allotmentLetter: "Allotment Letter", 
    }),
    []
  );

  // const DOCUMENT_TYPE_TO_FIELD = useMemo(
  //   () => ({
  //     "Sale Deed Certificate": "saledeedcertificate",
  //     "Sale Agreement": "saleagreement",
  //     "Property Tax Receipt": "propertytaxpaid",
  //     "Occupancy Certificate": "occupancycertificate",
  //     "Other Documents": "otherdocuments",
  //     "Allotment Letter": "allotmentLetter",
  //     "Khata Certificate": "khataCertificate",
  //   }),
  //   []
  // );

  const normalize = useCallback(
    (s) => String(s || "").trim().toLowerCase().replace(/[\s_]/g, ""),
    []
  );

  const getOptions = useCallback(
    (fieldKey) => {
      const match = normalize(FIELD_TO_ATTRIBUTE_MAP[fieldKey] || fieldKey);
      return attributeValues.filter(
        (it) => it?.isActive && normalize(it.attrName) === match
      );
    },
    [attributeValues, FIELD_TO_ATTRIBUTE_MAP, normalize]
  );

  // ------------------------------ VALIDATION
const validationRules = useMemo(
  () => ({
    allotmentLetter: { required: false },
    saledeedcertificate: { required: false },
    saleagreement: { required: false },
    propertytaxpaid: { required: false },
    occupancycertificate: { required: false },
    otherdocuments: { required: false },
  }),
  []
);
    const shouldShowSaleAgreement = useCallback(() => {
    const saleDeedOption = getOptions("saledeedcertificate").find(
      opt => String(opt.id) === String(form.saledeedcertificate)
    );
    return saleDeedOption?.label?.toLowerCase().includes("no");
  }, [form.saledeedcertificate, getOptions]);
  
  const getFieldError = useCallback(
    (field) => {
      const rule = validationRules[field];
      if (!rule) return null;

      const val = form[field];

      if (field === "saleagreement" && shouldShowSaleAgreement() && !val) {
        return rule.message;
      }
      
      if (rule.required && !val) return rule.message;
      return null;
    },
[form, validationRules, shouldShowSaleAgreement]  );



  const handleBlur = useCallback((f) => setTouched((p) => ({ ...p, [f]: true })), []);

  // 🔍 UPDATE FIELD FUNCTION
  const updateField = useCallback((key, value) => {
    console.log(`🔍 updateField: ${key} = ${value}`);
    setForm((prev) => { 
      const newForm = { ...prev, [key]: value };
      
      if (key === "saledeedcertificate") {
        const saleDeedOption = getOptions("saledeedcertificate").find(
          opt => String(opt.id) === String(value)
        );
        const hadNo = prev.saledeedcertificate && getOptions("saledeedcertificate").find(
          opt => String(opt.id) === String(prev.saledeedcertificate)
        )?.label?.toLowerCase().includes("no");
        
        const nowHasYes = saleDeedOption && !saleDeedOption.label.toLowerCase().includes("no");
        
        if (hadNo && nowHasYes) {
          newForm.saleagreement = "";
        }
      }
      
      return newForm;
    });
    setTouched((prev) => ({ ...prev, [key]: true }));
  }, [getOptions]);

  // 🔍 FETCH EXISTING DOCUMENTS
  const fetchExistingDocuments = useCallback(async () => {
    console.log('🔍 fetchExistingDocuments called with propertyUuid:', propertyUuid);
    
    if (!propertyUuid) {
      console.warn('⚠️ No propertyUuid provided, skipping document fetch');
      return;
    }

    try {
      const url = `/property-documents/?property_uuid=${propertyUuid}`;
      console.log('🔍 Fetching documents from:', url);
      
      const response = await callApi("GET", url);
      
      console.log('🔍 Document API Response:', {
        status: response?.status,
        statusCode: response?.status_code,
        hasData: !!response?.data,
        dataType: response?.data ? typeof response.data : 'null',
        isArray: Array.isArray(response?.data),
        dataLength: Array.isArray(response?.data) ? response.data.length : 'N/A',
      });
      
      if (Array.isArray(response.data)) {
        console.log('🔍 Processing document array, length:', response.data.length);
        
        if (response.data.length === 0) {
          console.log('ℹ️ No existing documents found for this property');
          setExistingDocuments([]);
          return;
        }
        
        const docs = response.data.map((doc, index) => {
          console.log(`🔍 Processing document ${index}:`, {
            PROPERTY_DOCUMENT_ID: doc.PROPERTY_DOCUMENT_ID,
            DOCUMENT_URL: doc.DOCUMENT_URL,
            ATTRIBUTE_VALUE_NAME: doc.ATTRIBUTE_VALUE_NAME,
            ATTRIBUTE_NAME: doc.ATTRIBUTE_NAME,
          });
          
          let documentType = doc.ATTRIBUTE_VALUE_NAME || doc.ATTRIBUTE_NAME || "General";
          
          const knownTypes = Object.values(FIELD_TO_DOCUMENT_TYPE);
          if (!knownTypes.includes(documentType)) {
            console.warn(`⚠️ Unknown document type: "${documentType}" - will be treated as "Other Documents"`);
            documentType = "Other Documents";
          }
          
          return {
            id: doc.PROPERTY_DOCUMENT_ID,
            documentId: doc.PROPERTY_DOCUMENT_ID,
            name: doc.DOCUMENT_URL ? doc.DOCUMENT_URL.split('/').pop() : "Document",
            type: documentType,
            category: "Document",
            url: doc.DOCUMENT_URL,
            date: doc.CREATED_TIMESTAMP,
            status: doc.STATUS || "Active",
            attributeId: doc.ATTRIBUTE_ID,
            attributeValueId: doc.ATTRIBUTE_VALUE_ID,
            propertyId: doc.PROPERTY_ID,
            propertyTitle: doc.PROPERTY_TITLE,
            attributeName: doc.ATTRIBUTE_NAME,
            raw: doc,
          };
        });
        
        console.log('🔍 Mapped documents:', docs);
        setExistingDocuments(docs);
      } else {
        console.warn('⚠️ Unexpected response data format:', response.data);
        setExistingDocuments([]);
      }
    } catch (err) {
      console.error('❌ Failed to fetch existing documents:', err);
      setError("Failed to fetch existing documents");
    }
  }, [propertyUuid, FIELD_TO_DOCUMENT_TYPE]);

  // 🔍 DELETE DOCUMENT FUNCTION
  const handleDeleteDocument = useCallback(async (documentId) => {
    console.log('🔍 handleDeleteDocument called with ID:', documentId);
    
    if (!documentId) {
      console.warn('⚠️ No document ID provided for deletion');
      return;
    }
    
    setDeletingDocId(documentId);
    try {
      console.log('🔍 Deleting document at:', `/property-documents/${documentId}`);
      await callApi("DELETE", `/property-documents/${documentId}`);
      
      console.log('✅ Document deleted successfully');
      
      setExistingDocuments(prev => {
        const filtered = prev.filter(doc => doc.id !== documentId);
        console.log('🔍 Updated existingDocuments after deletion:', filtered);
        return filtered;
      });
      
      setSuccess("Document deleted successfully");
    } catch (err) {
      console.error('❌ Failed to delete document:', err);
      setError("Failed to delete document");
    } finally {
      setDeletingDocId(null);
    }
  }, []);

  // 🔍 FETCH ADDITIONAL INFO DATA FROM BACKEND DIRECTLY
const fetchAdditionalInfoFromBackend = useCallback(async () => {
  if (!propertyUuid) {
    console.log('⚠️ No propertyUuid, skipping fetch');
    return;
  }

  setIsLoading(true);
  try {
    const response = await callApi("GET", `/properties/?property_uuid=${propertyUuid}`);
    console.log('🔍 Additional Info API Response:', response);

    if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
      const propertyData = response.data[0];
      const attributes = propertyData.attributes || {};
      const additionalInfo = propertyData.additional_information || {};

      console.log('🔍 Found additional info:', { attributes, additionalInfo });

      setForm((prev) => {
        const updatedForm = { ...prev };
        Object.entries(FIELD_TO_ATTRIBUTE_MAP).forEach(([fieldKey, apiKey]) => {
          const value = additionalInfo[apiKey] ?? attributes[apiKey];
          if (value !== undefined && value !== null && value !== "") {
            updatedForm[fieldKey] = String(value);
          }
        });
        return updatedForm;
      });
    } else {
      console.log('ℹ️ No additional info found for this property');
    }
  } catch (err) {
    console.error('❌ Failed to fetch additional info:', err);
  } finally {
    setIsLoading(false);
  }
}, [propertyUuid, FIELD_TO_ATTRIBUTE_MAP]);   // ✅ removed `form`

  // 🔍 HYDRATE FORM WITH EXISTING DATA - FIXED VERSION
const hydratedRef = useRef(false);

// Reset hydration guard when property changes
useEffect(() => {
  hydratedRef.current = false;
}, [propertyUuid]);

useEffect(() => {
  // Only hydrate once per propertyUuid
  if (hydratedRef.current) return;
  if (!propertyUuid && (!data || Object.keys(data).length === 0)) return;

  if (data && Object.keys(data).length > 0 && attributeValues.length > 0) {
    hydratedRef.current = true;

    const attributes = data.attributes || {};
    const additionalInfo = data.additional_information || {};

    setForm((prev) => {
      const updated = { ...prev };
      Object.entries(FIELD_TO_ATTRIBUTE_MAP).forEach(([fieldKey, apiKey]) => {
        const value = additionalInfo[apiKey] ?? attributes[apiKey];
        if (value !== undefined && value !== null && value !== "") {
          updated[fieldKey] = String(value);
        }
      });
      return updated;
    });
  } else if (propertyUuid && (!data || Object.keys(data).length === 0)) {
    hydratedRef.current = true;
    fetchAdditionalInfoFromBackend();
  }
}, [data, attributeValues, propertyUuid, FIELD_TO_ATTRIBUTE_MAP, fetchAdditionalInfoFromBackend]);
// ✅ removed `form`

  // 🔍 Fetch existing documents on mount and when propertyUuid changes
  useEffect(() => {
    console.log('🔍 Document Fetch Effect Triggered:', {
      propertyUuid,
      willFetch: !!propertyUuid
    });
    
    if (propertyUuid) {
      fetchExistingDocuments();
    } else {
      console.log('⚠️ No propertyUuid, skipping document fetch');
    }
  }, [propertyUuid, fetchExistingDocuments]);

  // 🔍 DOCUMENT HANDLING FUNCTIONS
  const handleFileUpload = useCallback((field, event) => {
    console.log(`🔍 handleFileUpload called for field: ${field}`);
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;

    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        setError(`File ${file.name} exceeds 10MB limit`);
        return false;
      }

      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'image/jpg',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];
      
      if (!allowedTypes.includes(file.type)) {
        setError(`File ${file.name} type not allowed (PDF, JPEG, PNG, Word only)`);
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) return;

    setError("");

    setDocumentUploads(prev => {
      const fieldData = prev[field] || { files: [] };
      const updated = {
        ...prev,
        [field]: {
          files: [...fieldData.files, ...validFiles]
        }
      };
      console.log(`🔍 Updated documentUploads for ${field}:`, updated[field]);
      return updated;
    });
    
    setUploadedFields(prev => ({
      ...prev,
      [field]: false
    }));
  }, []);

  const removeFile = useCallback((field, index) => {
    console.log(`🔍 removeFile: ${field} at index ${index}`);
    setDocumentUploads(prev => {
      const fieldData = prev[field] || { files: [] };
      const newFiles = fieldData.files.filter((_, i) => i !== index);
      
      return {
        ...prev,
        [field]: {
          files: newFiles
        }
      };
    });
    
    setUploadedFields(prev => {
      if (documentUploads[field]?.files.length <= 1) {
        return {
          ...prev,
          [field]: false
        };
      }
      return prev;
    });
  }, [documentUploads]);

  const viewFile = useCallback((file) => {
    const url = URL.createObjectURL(file);
    window.open(url, '_blank');
  }, []);

  const hasYesSelected = useCallback((field) => {
    const fieldValue = form[field];
    if (!fieldValue) return false;
    
    const options = getOptions(field);
    const selectedOption = options.find(opt => String(opt.id) === String(fieldValue));
    
    return selectedOption?.label?.toLowerCase() === "yes";
  }, [form, getOptions]);

  

  const hasOtherDocumentsSelected = useCallback(() => {
    const fieldValue = form.otherdocuments;
    if (!fieldValue) return false;
    
    const options = getOptions("otherdocuments");
    const selectedOption = options.find(opt => String(opt.id) === String(fieldValue));
    
    return selectedOption !== undefined;
  }, [form.otherdocuments, getOptions]);


  // 🔍 UPLOAD DOCUMENT FUNCTION
  const uploadDocument = useCallback(async (file, field) => {
    console.log(`🔍 uploadDocument: ${file.name} for field ${field}`);
    const formData = new FormData();
    
    formData.append('files', file);
    formData.append('property_uuid', propertyUuid);
    
    const documentType = FIELD_TO_DOCUMENT_TYPE[field] || "General Document";
    
    const docTypeOptions = attributeValues.filter(
      item => normalize(item.attrName) === normalize("document_type") ||
              normalize(item.attrName) === normalize("documenttype")
    );
    
    let attributeValueId = null;
    if (documentType && docTypeOptions.length > 0) {
      const selectedType = docTypeOptions.find(opt => 
        normalize(opt.label) === normalize(documentType)
      );
      if (selectedType) {
        attributeValueId = selectedType.id;
        console.log(`🔍 Found attribute_value_id ${attributeValueId} for document type: ${documentType}`);
      } else {
        console.warn(`⚠️ Could not find attribute_value_id for document type: ${documentType}`);
      }
    }
    
    const defaultAttributeValueId = attributeValueId || 2;
    formData.append('attribute_value_id', defaultAttributeValueId.toString());
    
    try {
      const response = await callApi("POST", "/property-documents/upload", formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('🔍 Upload response:', response);

      if (response.data?.success || response.status_code === 200) {
        return {
          success: true,
          documents: response.data?.documents || [],
          message: response.data?.message || "Upload successful"
        };
      }
      
      throw new Error(response.data?.message || "Upload failed");
      
    } catch (err) {
      console.error('❌ Upload failed:', err);
      if (err.response?.data?.data?.errors) {
        const errorMessages = err.response.data.data.errors.map(e => `${e.field}: ${e.message}`).join(', ');
        throw new Error(`Failed to upload ${file.name}: ${errorMessages}`);
      }
      throw new Error(`Failed to upload ${file.name}: ${err.message}`);
    }
  }, [propertyUuid, attributeValues, normalize, FIELD_TO_DOCUMENT_TYPE]);

  // 🔍 SAVE FUNCTION - Also refresh data after save
  const handleSave = useCallback(
    async (navigateNext = false) => {
      console.log('🔍 handleSave called, navigateNext:', navigateNext);
      
      setTouched(
        Object.keys(form).reduce((a, k) => ({ ...a, [k]: true }), {})
      );



      setSaving(true);
      setError("");
      setSuccess("");

      try {
        let uuid = propertyUuid;

        if (!uuid) {
          console.log('🔍 Creating new property...');
          uuid = await createProperty();
          onSaveSuccess?.(uuid);
        }

        // Upload documents for fields with YES selected
        const uploadPromises = [];
        const fieldsToUpload = [];
        
        Object.keys(FIELD_TO_ATTRIBUTE_MAP).forEach(field => {
          const fieldUploadData = documentUploads[field] || { files: [] };
          
          let shouldUpload = false;
          
          if (field === "otherdocuments") {
  shouldUpload =
    hasOtherDocumentsSelected() &&
    fieldUploadData.files.length > 0 &&
    !uploadedFields[field];


} else {
  shouldUpload =
    hasYesSelected(field) &&
    fieldUploadData.files.length > 0 &&
    !uploadedFields[field];
}
          
          if (shouldUpload) {
            console.log(`🔍 Will upload ${fieldUploadData.files.length} file(s) for ${field}`);
            fieldUploadData.files.forEach(file => {
              uploadPromises.push(uploadDocument(file, field));
            });
            fieldsToUpload.push(field);
          }
        });

        if (uploadPromises.length > 0) {
          setUploading(true);
          const results = await Promise.allSettled(uploadPromises);
          
          const successfulUploads = results.filter(r => r.status === 'fulfilled' && r.value.success);
          const failedUploads = results.filter(r => r.status === 'rejected');
          
          if (failedUploads.length > 0) {
            failedUploads.forEach(failure => {
              setError(failure.reason?.message || "Some uploads failed");
            });
          }
          
          if (successfulUploads.length > 0) {
            setSuccess(`Successfully uploaded ${successfulUploads.length} document(s)`);
            
            fieldsToUpload.forEach(field => {
              setUploadedFields(prev => ({
                ...prev,
                [field]: true
              }));
            });
          }
          setUploading(false);
        }

        // Save form attributes
        const attributes = {};
        Object.entries(FIELD_TO_ATTRIBUTE_MAP).forEach(([key, apiKey]) => {
          const val = form[key];
          
          if (val !== "" && val !== undefined && val !== null) {
            attributes[apiKey] = isNaN(Number(val)) ? val : Number(val);
          }
        });

        console.log('🔍 Saving attributes:', attributes);
        await saveSectionAttributes(uuid, attributes, form, "additional_information");

        if (uploadPromises.length === 0) {
          setSuccess("Additional information saved successfully!");
        }
        
        // 🔥 CRITICAL FIX: Refresh the data after save to ensure it's available when coming back
        await fetchAdditionalInfoFromBackend();
        await fetchExistingDocuments();
        
        onComplete?.(form);

        if (navigateNext) {
          onSaved?.(uuid, form);
          onNavigateNext?.(form);
        }

        const updatedDocumentUploads = { ...documentUploads };
        fieldsToUpload.forEach(field => {
          if (uploadedFields[field]) {
            updatedDocumentUploads[field] = { files: [] };
          }
        });
        
        setDocumentUploads(updatedDocumentUploads);

      } catch (err) {
        console.error('❌ Save failed:', err);
        setError(err.message || "Failed to save additional information");
      } finally {
        setSaving(false);
        setUploading(false);
      }
    },
[
  form,
  propertyUuid,
  createProperty,
  onSaveSuccess,
  saveSectionAttributes,
  onComplete,
  onSaved,
  onNavigateNext,
  FIELD_TO_ATTRIBUTE_MAP,
  documentUploads,
  hasYesSelected,
  hasOtherDocumentsSelected,
  uploadDocument,
  fetchExistingDocuments,
  fetchAdditionalInfoFromBackend,
  uploadedFields,
]
  );

  // 🔍 Initialize CommonFields
  const { renderDropdown } = CommonFields({
    form,
    touched,
    getFieldError,
    handleBlur,
    updateField,
    getOptions,
  });

  // 🔍 RENDER DOCUMENT UPLOAD SECTION
  const renderDocumentUpload = useCallback((field) => {
    console.log(`🔍 renderDocumentUpload called for field: ${field}`);
    const uploadData = documentUploads[field] || { files: [] };
    
    let shouldShowUpload = false;
    
if (field === "otherdocuments") {
  shouldShowUpload = hasOtherDocumentsSelected();
} else {
  shouldShowUpload = hasYesSelected(field);
}
    
    console.log(`🔍 Should show upload for ${field}:`, shouldShowUpload);
    
    if (!shouldShowUpload) return null;

    const documentType = FIELD_TO_DOCUMENT_TYPE[field] || "Document";
    const isUploaded = uploadedFields[field];

    return (
      <div style={{ marginTop: '1rem', padding: '1rem', border: '1px solid #e0e0e0', borderRadius: '4px', backgroundColor: '#f8f8f8' }}>
        <h6 style={{ marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600' }}>Upload {documentType}</h6>
        
        {isUploaded && (
          <div style={{ padding: '0.5rem', backgroundColor: '#e0f7fa', borderRadius: '4px', marginBottom: '1rem' }}>
            <p style={{ fontSize: '0.875rem', color: '#007d79', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>✓</span>
              <span>Document has been uploaded successfully. You can upload another if needed.</span>
            </p>
          </div>
        )}
        
        <FileUploader
          labelText={`Drag and drop ${documentType.toLowerCase()} or click to browse`}
          buttonLabel="Add file"
          buttonKind="primary"
          size="sm"
          filenameStatus="edit"
          accept={[
            '.pdf',
            '.jpg',
            '.jpeg',
            '.png',
            '.doc',
            '.docx'
          ]}
          multiple={false}
          onChange={(e) => handleFileUpload(field, e)}
          disabled={uploading || saving}
          style={{ marginBottom: '1rem' }}
        />
        
        {uploadData.files.length > 0 && (
          <div>
            <p style={{ fontSize: '0.875rem', marginBottom: '0.5rem', fontWeight: '500' }}>
              Files to upload:
            </p>
            {uploadData.files.map((file, index) => (
              <div
                key={index}
                style={{
                  padding: '0.5rem',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#ffffff',
                  marginBottom: '0.5rem'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Document size={16} />
                  <span style={{ fontSize: '0.875rem' }}>{file.name}</span>
                  <Tag type="gray" size="sm" style={{ fontSize: '0.75rem' }}>
                    {(file.size / 1024).toFixed(1)} KB
                  </Tag>
                </div>
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                  <Button
                    kind="ghost"
                    size="sm"
                    hasIconOnly
                    iconDescription="View file"
                    onClick={() => viewFile(file)}
                    renderIcon={View}
                  />
                  <Button
                    kind="danger--ghost"
                    size="sm"
                    hasIconOnly
                    iconDescription="Remove file"
                    onClick={() => removeFile(field, index)}
                    renderIcon={TrashCan}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }, [
  documentUploads,
  hasOtherDocumentsSelected,
  hasYesSelected,
  FIELD_TO_DOCUMENT_TYPE,
  uploadedFields,
  uploading,
  saving,
  handleFileUpload,
  viewFile,
  removeFile
]);

  // 🔍 RENDER EXISTING DOCUMENTS
  const renderExistingDocuments = useCallback((field) => {
    console.log(`🔍 renderExistingDocuments called for field: ${field}`);
    
    const expectedDocumentType = FIELD_TO_DOCUMENT_TYPE[field];
    console.log(`🔍 Looking for document type: ${expectedDocumentType}`);
    
    const filteredDocs = existingDocuments.filter(doc => {
      const typeMatches = doc.type === expectedDocumentType;
      const isOtherDocuments = field === "otherdocuments" && 
        !Object.values(FIELD_TO_DOCUMENT_TYPE).includes(doc.type);
      
      return typeMatches || isOtherDocuments;
    });

    console.log(`🔍 Filtered documents for ${field}:`, filteredDocs);

    if (filteredDocs.length === 0) {
      return null;
    }

    return (
      <div style={{ marginTop: '1rem' }}>
        <h6 style={{ marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600' }}>
          Uploaded {FIELD_TO_DOCUMENT_TYPE[field]} ({filteredDocs.length})
        </h6>
        {filteredDocs.map((doc, index) => (
          <div
            key={doc.id || index}
            style={{
              padding: '0.5rem',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#ffffff',
              marginBottom: '0.5rem'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Document size={16} />
              <span style={{ fontSize: '0.875rem' }}>{doc.name}</span>
              <Tag type="gray" size="sm" style={{ fontSize: '0.75rem' }}>
                Uploaded
              </Tag>
            </div>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <Button
                kind="ghost"
                size="sm"
                hasIconOnly
                iconDescription="View file"
                onClick={() => {
                  console.log(`🔍 Opening document URL: ${doc.url}`);
                  window.open(doc.url, '_blank');
                }}
                renderIcon={View}
                disabled={!doc.url}
              />
              <Button
                kind="danger--ghost"
                size="sm"
                hasIconOnly
                iconDescription="Delete file"
                onClick={() => handleDeleteDocument(doc.id)}
                renderIcon={TrashCan}
                disabled={deletingDocId === doc.id}
              />
            </div>
          </div>
        ))}
      </div>
    );
  }, [existingDocuments, FIELD_TO_DOCUMENT_TYPE, handleDeleteDocument, deletingDocId]);

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <div style={{ padding: "4rem", textAlign: "center" }}>
        <Loading description="Loading additional information..." />
      </div>
    );
  }

  // 🔍 MAIN RENDER
  return (
    <>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          lowContrast
          timeout={5000}
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          lowContrast
          timeout={3000}
          onClose={() => setSuccess("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
        <Grid>
          <Column sm={4} md={8} lg={16}>
            <Tile style={{ padding: "2rem", backgroundColor:"white"}}>
              <Stack gap={6}>
              
             
                

                {uploading && (
                  <div style={{ marginBottom: '1rem' }}>
                    <Loading description="Uploading files..." withOverlay={false} />
                    <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
                      Please wait while documents are being uploaded...
                    </p>
                  </div>
                )}

                {deletingDocId && (
                  <div style={{ marginBottom: '1rem' }}>
                    <Loading description="Deleting document..." withOverlay={false} />
                  </div>
                )}

               <FormGroup>
                  <Grid>
                    <Column sm={4} md={4} lg={8}>
                      {renderDropdown("allotmentLetter", "Do you have Allotment Letter?", false)}
                      {renderDocumentUpload("allotmentLetter")}
                      {renderExistingDocuments("allotmentLetter")}
                    </Column>
                                        <Column sm={4} md={4} lg={8}>
                      {renderDropdown("occupancycertificate", "Do You have Occupancy Certificate?",false)}
                      {renderDocumentUpload("occupancycertificate")}
                      {renderExistingDocuments("occupancycertificate")}
                    </Column>
                  </Grid>
                </FormGroup>
                
                <FormGroup>
                  <Grid>
                    <Column sm={4} md={4} lg={8}>
                      {renderDropdown("saledeedcertificate", "Do You have Sale Deed Certificate?", false)}
                      {renderDocumentUpload("saledeedcertificate")}
                      {renderExistingDocuments("saledeedcertificate")}
                    </Column>
                    <Column sm={4} md={4} lg={8}>
                      {shouldShowSaleAgreement() && 
                        <>
                          {renderDropdown("saleagreement", "Do You have Sale Agreement?", false)}
                          {renderDocumentUpload("saleagreement")}
                          {renderExistingDocuments("saleagreement")}
                        </>
                      }
                    </Column>
                  </Grid>
                </FormGroup>
                
                <FormGroup>
                  <Grid>
                    <Column sm={4} md={4} lg={8}>
                      {renderDropdown("propertytaxpaid", "Have you paid Property Tax?",false)}
                      {renderDocumentUpload("propertytaxpaid")}
                      {renderExistingDocuments("propertytaxpaid")}
                    </Column>
                  </Grid>
                </FormGroup>

                <FormGroup>
                  <Column lg={8} md={4} sm={4}>
                    {renderDropdown("otherdocuments", "Any Other Documents to Upload?", false)}
                    {renderDocumentUpload("otherdocuments")}
                    {renderExistingDocuments("otherdocuments")}
                  </Column>
                </FormGroup>

                {CommonButtons && (
                  <CommonButtons
                    onSave={() => handleSave(false)}
                    onNext={() => handleSave(true)}
                    showBack={true}
                    showSave={true}
                    showNext={true}
                    isSaveLoading={saving || uploading}
                  />
                )}
              </Stack>
            </Tile>
          </Column>
        </Grid>
      </div>
    </>
  );
};

export default AdditionalInfo;